export type MessageType = 'ZBPMessage';

export const allowedAttachmentsMimeTypesZBPMessage: Array<string> = [
  'pdf',
  'xml',
  'csv',
  'png',
  'jpeg',
  'jpg'
];

export const maxSizePerAttachmentZBPMessage: number = 2 * 1024 * 1024; // 2 MiB — enforced by antragssystem receiver

export const maxNumberAttachmentsZBPMessage: number = 5; // enforced by antragssystem receiver

export const maxSizeZBPMessage: number = 1 * 1024 * 1024; // 1 MiB in byte

export const ZBPAlertTextSnippet: string =
  'das Zentrale Bürgerpostfach (BundID)';

function checkNoSizePerAttachment(currentFiles: File[]): string | null {
  let counter: number = 0;
  for (let i = 0; i < currentFiles.length; i++) {
    if (currentFiles[i].size <= 0) {
      counter++;
    }
  }

  if (counter !== 0) {
    if (counter === 1) {
      return 'Ein Anhang ist leer. Keine Anhänge wurden hinzugefügt!';
    } else {
      return `${counter} Anhänge sind leer. Keine Anhänge wurden hinzugefügt!`;
    }
  }

  return null;
}

function checkMaxSizePerAttachment(
  currentFiles: File[],
  maxSizePerAttachment: number,
  alertTextSnippet: string
): string | null {
  const alertText: string = `Eine einzelne Datei als Anhang einer Nachricht an ${alertTextSnippet} kann maximal ${maxSizePerAttachment / 1024 / 1024} MB groß sein!`;
  for (let i = 0; i < currentFiles.length; i++) {
    if (currentFiles[i].size > maxSizePerAttachment) {
      return alertText;
    }
  }

  return null;
}

function checkMaxNumberAttachments(
  currentFiles: File[],
  otherFileSizes: Array<number>,
  maxNumberAttachments: number,
  alertTextSnippet: string
): string | null {
  const alertText: string = `Eine Nachricht an ${alertTextSnippet} kann maximal ${maxNumberAttachments} Anhänge haben!`;
  if (otherFileSizes.length + currentFiles.length > maxNumberAttachments) {
    return alertText;
  }
  return null;
}

function checkAllowedAttachmentsMimeTypes(
  currentFiles: File[],
  allowedAttachmentsMimeTypes: Array<string>,
  alertTextSnippet: string
): string | null {
  const notAllowedExtensionsStr: Array<string> = [];
  let currentFileHasNoExtension: boolean = false;
  for (let i = 0; i < currentFiles.length; i++) {
    const fileExtension: string | undefined = currentFiles[i].name
      .split('.')
      .pop()
      ?.toLowerCase();
    if (fileExtension) {
      if (
        !allowedAttachmentsMimeTypes.includes(fileExtension ?? '') &&
        !notAllowedExtensionsStr.includes(fileExtension)
      ) {
        notAllowedExtensionsStr.push(fileExtension);
      }
    } else {
      currentFileHasNoExtension = true;
    }
  }
  const alertText: string = `als Anhang bei einer Nachricht an ${alertTextSnippet} unzulässig!`;
  if (currentFileHasNoExtension && notAllowedExtensionsStr.length !== 0) {
    return `Kein Dateityp und ${notAllowedExtensionsStr.join(', ')} sind ${alertText}`;
  } else if (currentFileHasNoExtension) {
    return `Kein Dateityp ist ${alertText}`;
  } else if (notAllowedExtensionsStr.length === 1) {
    return `Der Dateityp ${notAllowedExtensionsStr[0]} ist ${alertText}`;
  } else if (notAllowedExtensionsStr.length > 1) {
    return `Die Dateitypen ${notAllowedExtensionsStr.join(', ')} sind ${alertText}`;
  }

  return null;
}

function checkNoDuplicateFileNames(
  currentFiles: File[],
  otherFileNames: Array<string>
): string | null {
  const seenFileNames: Set<string> = new Set(
    otherFileNames.map((fileName) => fileName.toLowerCase())
  );
  const duplicateFileNames: Array<string> = [];
  for (let i = 0; i < currentFiles.length; i++) {
    const fileName: string = currentFiles[i].name;
    const lowerCaseFileName: string = fileName.toLowerCase();
    if (
      seenFileNames.has(lowerCaseFileName) &&
      !duplicateFileNames.some(
        (duplicateFileName) =>
          duplicateFileName.toLowerCase() === lowerCaseFileName
      )
    ) {
      duplicateFileNames.push(fileName);
    }
    seenFileNames.add(lowerCaseFileName);
  }

  if (duplicateFileNames.length === 0) {
    return null;
  }
  if (duplicateFileNames.length === 1) {
    return `Eine Anlage mit dem Namen "${duplicateFileNames[0]}" ist bereits vorhanden. Jede Anlage muss einen eindeutigen Namen haben!`;
  }
  return `Anlagen mit den Namen ${duplicateFileNames.map((fileName) => `"${fileName}"`).join(', ')} sind bereits vorhanden. Jede Anlage muss einen eindeutigen Namen haben!`;
}

export const getAlertTexts = (
  currentFiles: File[],
  otherFileSizes: Array<number>,
  messageType: MessageType,
  otherFileNames: Array<string> = []
): Array<string> => {
  switch (messageType) {
    case 'ZBPMessage':
      return [
        checkNoSizePerAttachment(currentFiles),
        checkMaxNumberAttachments(
          currentFiles,
          otherFileSizes,
          maxNumberAttachmentsZBPMessage,
          ZBPAlertTextSnippet
        ),
        checkAllowedAttachmentsMimeTypes(
          currentFiles,
          allowedAttachmentsMimeTypesZBPMessage,
          ZBPAlertTextSnippet
        ),
        checkMaxSizePerAttachment(
          currentFiles,
          maxSizePerAttachmentZBPMessage,
          ZBPAlertTextSnippet
        ),
        checkNoDuplicateFileNames(currentFiles, otherFileNames)
      ].filter((value): value is string => value !== null);
  }
};
